import { useInfiniteQuery } from '@tanstack/vue-query'
import { refDebounced } from '@vueuse/core'
import { useSearchParams } from '~/composables/search-params'

type ListResponse<T = any> = {
    totalItems: number
    offset: number
    limit: number
    items: T[]
}

/**
 * Hooks that provide autocomplete suggestions
 */

/**
 * Find technic articles based on a user input
 */
export function useInfiniteTechnicArticleSuggestions(_query: globalThis.Ref<string>) {
    const query = computed(() => _query.value)
    const limit = ref(10)

    const debouncedInput = refDebounced(query, 500)

    const { data, ...queryResult } = useInfiniteQuery({
        queryKey: ['search', 'technic-articles', debouncedInput, limit],
        queryFn: async ({ pageParam }) => {
            return await $fetch<ListResponse>('/api/technic/search', {
                query: {
                    searchTerm: debouncedInput.value,
                    offset: pageParam,
                    limit: limit.value
                }
            })
        },
        getNextPageParam: (lastPage, _pages) => {
            const offset = lastPage.offset + limit.value
            return offset < lastPage.totalItems ? offset : undefined
        },
        initialPageParam: 0,
        staleTime: Infinity,
        enabled: computed(() => debouncedInput.value.length > 0),
        placeholderData: (prev) => prev,
        select: (data) => {
            const options: ApiModel<"TechnicArticle">[] = []
            data.pages.forEach(({ items }, index) => {
                options.push(...items)
            })

            return useModelFactory('TechnicArticle').fromJson(options)
        }
    })

    return {
        query,
        technicArticles: computed(() => data.value ?? []),
        ...queryResult,
        limit
    }
}

/**
 * Find members based on a user input
 */
export function useInfiniteMemberSuggestions(_query: globalThis.Ref<string>) {
    const query = computed(() => _query.value || '')
    const scope = ref<'GLOBAL_MEMBERS' | 'MY_MEMBERS'>('GLOBAL_MEMBERS')
    const limit = ref(10)

    // Initialize input with either debounce or throttle based on the flag
    const debouncedInput = refDebounced(query, 500)

    const { data, ...queryResult } = useInfiniteQuery({
        queryKey: ['search', 'members', debouncedInput, scope, limit],

        queryFn: async ({ pageParam }) => {
            return await $fetch<ListResponse>('/api/members/search', {
                query: {
                    searchTerm: debouncedInput.value,
                    searchScope: scope.value,
                    offset: pageParam,
                    limit: limit.value
                }
            })
        },

        getNextPageParam: (lastPage, _pages) => {
            const offset = lastPage.offset + limit.value
            return offset < lastPage.totalItems ? offset : undefined
        },
        initialPageParam: 0,
        staleTime: Infinity,
        enabled: computed(() => debouncedInput.value.length > 0),
        placeholderData: (prev) => prev,
        select: (data) => {
            const options: ApiModel<'MemberData'>[] = []
            data.pages.forEach(({ items }, index) => {
                options.push(...items)
            })

            return useModelFactory('MemberData').fromJson(options)
        }
    })

    return {
        scope,
        query,
        members: computed(() => data.value ?? []),
        ...queryResult,
        limit,
        debouncedInput
    }
}

/**
 * Find events based on a user input
 */
export function useInfiniteEventsSuggestions(_query: globalThis.Ref<string>) {
    const query = computed(() => _query.value ?? '')
    const limit = ref(10)

    const debouncedInput = refDebounced(query, 500)

    const sortBy = useSearchParams({
        field: 'DATE_FROM',
        directionAsc: false
    }).serializeObject()

    const { data, ...queryResult } = useInfiniteQuery({
        queryKey: ['search', 'events', debouncedInput, limit],
        queryFn: async ({ pageParam }) => {
            return await $fetch<ListResponse>('/api/events', {
                query: {
                    searchTerm: debouncedInput.value,
                    offset: pageParam,
                    limit: limit.value,
                    sortBy
                }
            })
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages) => {
            const offset = lastPage.offset + limit.value
            return offset < lastPage.totalItems ? offset : undefined
        },
        staleTime: Infinity,
        enabled: computed(() => debouncedInput.value.length > 0),
        placeholderData: (prev) => prev,
        select: (data) => {
            const options: ApiModel<'Event'>[] = []
            data.pages.forEach(({ items }, index) => {
                options.push(...items)
            })

            return useModelFactory('Event').fromJson(options)
        }
    })

    return {
        query,
        events: computed(() => data.value ?? []),
        limit,
        ...queryResult,
    }
}

/**
 * Find adress based on a user input
 */

export function useInfiniteAdressSuggestions(_query: globalThis.Ref<string>) {
    const query = computed(() => _query.value)
    const limit = ref(10)

    const debouncedInput = refDebounced(query, 500)

    const { data, ...queryResult } = useInfiniteQuery({
        queryKey: ['search', 'address', debouncedInput, limit],

        queryFn: async ({ pageParam }) => {
            return await $fetch<ListResponse>('/api/address/search', {
                query: {
                    searchTerm: debouncedInput.value,
                    offset: pageParam,
                    limit: limit.value
                }
            })
        },

        getNextPageParam: (lastPage, _pages) => {
            const offset = lastPage.offset + limit.value
            return offset < lastPage.totalItems ? offset : undefined
        },
        initialPageParam: 0,
        staleTime: Infinity,
        enabled: computed(() => debouncedInput.value.length > 0),
        placeholderData: (prev) => prev,
        select: (data) => {
            const options: ApiModel<'AddressContact'>[] = []
            data.pages.forEach(({ items }, index) => {
                options.push(...items)
            })

            return useModelFactory('AddressContact').fromJson(options)
        }
    })

    return {
        query,
        addressContacts: computed(() => data.value ?? []),
        ...queryResult,
        limit
    }
}
